import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import { PaymentDetailsCartState } from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import { UsePaymentDetailsFormState } from "@steiner/Overrides/Hooks/Payments/usePaymentDetailsFormState";
import {
    isPORequired,
    isReleaseNumberRequired,
} from "@steiner/Overrides/Widgets/CheckoutReviewAndSubmit/checkout.helper";
import { shallowEqual } from "react-redux";

export interface UsePaymentDetailsFormValidation {
    validatePaymentMethod: (paymentMethod: string) => boolean;
    validatePONumber: (poNumber: string) => boolean;
    validateAddress1: (address1: string) => boolean;
    validateCountry: (countryId: string) => boolean;
    validateState: (stateId: string) => boolean;
    validateCity: (city: string) => boolean;
    validatePostalCode: (postalCode: string) => boolean;
    /**
     * XNG-Changes
     */
    validateReleaseNumber: (releaseNumber: string) => boolean;
}

function usePaymentDetailsFormValidation(
    paymentDetailsFormState: UsePaymentDetailsFormState,
    paymentDetailsCartState: PaymentDetailsCartState,
): UsePaymentDetailsFormValidation {
    const { cart, paymentMethods, isCreditCard, isECheck, selectedCountry } = paymentDetailsCartState;
    const { useBillingAddress, isPayPal, externalTokenProvider } = paymentDetailsFormState;

    /**
     * XNG-Changes
     */
    const { billToState } = useAppSelector(
        (state: ApplicationState) => ({
            billToState: getBillToState(state, cart ? cart.billToId : undefined),
        }),
        shallowEqual,
    );

    const validatePaymentMethod = (paymentMethod: string) => {
        const paymentMethodValid =
            isPayPal || !paymentMethods || paymentMethods.length === 0 || isNonEmptyString(paymentMethod);
        paymentDetailsFormState.setPaymentMethodError(
            !paymentMethodValid ? translate("Payment Method is required.") : "",
        );
        return paymentMethodValid;
    };

    const validatePONumber = (poNumber: string) => {
        const isRequired = isPORequired(billToState?.value);

        const poNumberValid =
            !cart || !cart.showPoNumber || !(cart.requiresPoNumber || isRequired) || isNonEmptyString(poNumber);
        paymentDetailsFormState.setPONumberError(!poNumberValid ? translate("PO Number is required.") : "");
        return poNumberValid;
    };

    const validateAddress1 = (address1: string) => {
        const address1Valid =
            !(isCreditCard || isECheck || externalTokenProvider) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(address1));
        paymentDetailsFormState.setAddress1Error(!address1Valid ? translate("Address is required.") : "");
        return address1Valid;
    };

    const validateCountry = (countryId: string) => {
        const countryValid =
            !(isCreditCard || isECheck || externalTokenProvider) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(countryId));
        paymentDetailsFormState.setCountryError(!countryValid ? translate("Country is required.") : "");
        return countryValid;
    };

    const validateState = (stateId: string) => {
        const stateValid =
            (!!selectedCountry && !!selectedCountry.states && selectedCountry.states.length <= 0) ||
            !(isCreditCard || isECheck) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(stateId));
        paymentDetailsFormState.setStateError(!stateValid ? translate("State is required.") : "");
        return stateValid;
    };

    const validateCity = (city: string) => {
        const cityValid =
            !(isCreditCard || isECheck || externalTokenProvider) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(city));
        paymentDetailsFormState.setCityError(!cityValid ? translate("City is required.") : "");
        return cityValid;
    };

    const validatePostalCode = (postalCode: string) => {
        const postalCodeValid =
            !(isCreditCard || isECheck || externalTokenProvider) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(postalCode));
        paymentDetailsFormState.setPostalCodeError(!postalCodeValid ? translate("Postal Code is required.") : "");
        return postalCodeValid;
    };

    /**
     * XNG-Changes
     */
    const validateReleaseNumber = (releaseNumber: string) => {
        const isRequired = isReleaseNumberRequired(billToState?.value);

        const releaseNumberValid = !cart || !isRequired || isNonEmptyString(releaseNumber);
        paymentDetailsFormState.setReleaseNumberError(
            !releaseNumberValid ? translate("Job # / Release # is required.") : "",
        );
        return releaseNumberValid;
    };

    return {
        validatePaymentMethod,
        validatePONumber,
        validateAddress1,
        validateCountry,
        validateState,
        validateCity,
        validatePostalCode,
        validateReleaseNumber,
    };
}

export default usePaymentDetailsFormValidation;
